import {
  Dispatch,
  MouseEvent,
  ReactNode,
  SetStateAction,
  useState,
} from "react";
import { Updater, useImmer } from "use-immer";
import {
  AppBar,
  Avatar,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { formatHMS } from "../utils/formatHMS";
import {
  AccessAlarm,
  ArrowDropDownOutlined,
  DarkModeOutlined,
  LightModeOutlined,
  Menu as MenuIcon,
  MoreHorizOutlined,
  PersonOutlined,
  Poll,
} from "@mui/icons-material";
import FlexBetween from "@shared/components/FlexBetween";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { clearAll, logout } from "@store/slices/authSlice";

import { changeMode } from "@store/slices/themeSlice";
import BaseViewToggle from "./BaseViewToggle";
import ResourceSelector from "./ResourceSelector";
import PaperEditorButton from "./PaperEditorButton";
import { setBaseView } from "@store/slices/viewSlice";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import WorkspaceDropdown from "./WorkspaceDropdown";
import DemoBanner from "./DemoBanner";
import { saveAllDirtyResources } from "@store/thunks/workspaceThunks";
import { selectIsDemoMode } from "@store/slices/viewSlice";
import { startTour } from "@features/onboarding/state/onboardingSlice";

interface Props {
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
  sidebarWidth: number;
  isNonMobile: boolean;
  onExportClick: () => void;
  modals: (
    modalVisible: string,
    setModalVisible: Updater<string>
  ) => ReactNode[];
  canvasOn: boolean;
}

const ArgNavbar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  sidebarWidth,
  onExportClick,
  modals,
  canvasOn,
}: Props) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const userState = useAppSelector((state) => state.user);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const themeState = useAppSelector((state) => state.theme);
  const stopwatchState = useAppSelector((state) => state.stopwatch);
  const isDemoMode = useAppSelector(selectIsDemoMode);
  const [modalVisible, setModalVisible] = useImmer("");
  const navigate = useNavigate();

  // The "profile" trigger is really an account menu (nav shortcuts +
  // support + sign-out), so present it as the user's account: an avatar
  // (photo, or initials, falling back to a generic person icon) plus a
  // name/email header inside the menu.
  const accountName = userInfo?.full_name || userInfo?.username || null;
  const accountInitials = (() => {
    const src = (userInfo?.full_name || userInfo?.username || "").trim();
    if (!src) return null;
    const parts = src.split(/\s+/).filter(Boolean);
    const first = parts[0] ?? "";
    const last = parts.length >= 2 ? parts[parts.length - 1] ?? "" : "";
    const letters = last ? first.charAt(0) + last.charAt(0) : src.slice(0, 2);
    return letters.toUpperCase();
  })();

  const [profileAnchor, setProfileAnchor] = useState<any | null>(null);
  const isProfileOpen = Boolean(profileAnchor);
  const handleProfileClick = (event: MouseEvent<HTMLButtonElement>) =>
    setProfileAnchor(event.currentTarget);
  const handleProfileClose = () => setProfileAnchor(null);

  const [toolsAnchor, setToolsAnchor] = useState<any | null>(null);
  const isToolsOpen = Boolean(toolsAnchor);
  const handleToolsClick = (event: MouseEvent<HTMLButtonElement>) =>
    setToolsAnchor(event.currentTarget);
  const handleToolsClose = () => setToolsAnchor(null);

  const handleLogout = () => {
    // Fire the save in the background. Blocking the logout on a
    // potentially slow or 401-ing save lets a stale "finally" fire
    // after the user has signed back in, clobbering the new session
    // — best-effort save is the right trade. At worst the user loses
    // a few seconds of edits the auto-save interval hadn't picked up.
    dispatch(saveAllDirtyResources())
      .unwrap()
      .catch((error) => {
        console.error("Error saving data on logout:", error);
      });
    dispatch(clearAll());
    dispatch(logout());
    navigate("/");
  };

  const handleDemoQuit = () => {
    navigate("/");
    dispatch(clearAll());
  };
  const handleStopWatch = () => {
    setModalVisible("watch");
  };

  const handleBaseViewChange = (value: 'reading' | 'canvas') => {
    dispatch(setBaseView(value));
  };

  const handleToolSelect = (type: string) => {
    setModalVisible(type);
    handleToolsClose();
  };

  return (
    <AppBar
      sx={{
        marginLeft: isSidebarOpen ? `${sidebarWidth}px` : 0,
        width: isSidebarOpen ? `calc(100% - ${sidebarWidth}px)` : "100%",
        transition: theme.transitions.create(["margin", "width"], {
          easing: isSidebarOpen
            ? theme.transitions.easing.easeOut
            : theme.transitions.easing.sharp,
          duration: isSidebarOpen
            ? theme.transitions.duration.enteringScreen
            : theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      {isDemoMode && <DemoBanner />}
      <Toolbar>
        {/* Left Side */}
        <FlexBetween sx={{ marginRight: "auto", gap: 2 }}>
          {!isSidebarOpen && (
            <Tooltip title="Open Sidebar">
              <IconButton onClick={() => setIsSidebarOpen(true)}>
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isDemoMode && <WorkspaceDropdown />}
          <BaseViewToggle
            value={canvasOn ? "canvas" : "reading"}
            onChange={handleBaseViewChange}
          />
          {!isDemoMode && <ResourceSelector />}
        </FlexBetween>
        {/* Right Side */}
        <FlexBetween gap="1rem" sx={{ marginLeft: "auto" }}>
          <PaperEditorButton />
          {/* Hidden in demo: sessions are per-(user, reading) server
              state, and the demo's non-UUID ids can't reach the
              reading routes at all. A local-only mock would show a
              session history that silently vanishes on refresh. */}
          {userState.userId && (
            <Tooltip
              title={
                stopwatchState.isRunning
                  ? `Timer running — ${formatHMS(stopwatchState.time)}`
                  : "Timer"
              }
            >
              <IconButton onClick={handleStopWatch}>
                {/* Per-mode "active" green (see theme.ts contrast
                    notes) marks a running timer; the tooltip carries
                    the elapsed time. */}
                <AccessAlarm
                  sx={{
                    color: stopwatchState.isRunning
                      ? "success.main"
                      : "inherit",
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={themeState.mode === "dark" ? "Toggle Light" : "Toggle Dark"}
          >
            <IconButton onClick={() => dispatch(changeMode())}>
              {theme.palette.mode === "dark" ? (
                <DarkModeOutlined />
              ) : (
                <LightModeOutlined />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Provide Feedback">
            <IconButton
              onClick={() =>
                window.open("https://forms.gle/QmW5XQamuJB2s2hE9", "_blank")
              }
            >
              <Poll />
            </IconButton>
          </Tooltip>
          <Box>
            <Tooltip title="More Tools">
              <IconButton onClick={handleToolsClick}>
                <MoreHorizOutlined />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={toolsAnchor}
              open={isToolsOpen}
              onClose={handleToolsClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              {!isDemoMode && (
                <MenuItem onClick={() => handleToolSelect("editCategories")}>
                  Edit Categories
                </MenuItem>
              )}
              <MenuItem onClick={onExportClick}>Export Canvas</MenuItem>
              <MenuItem
                onClick={() => handleToolSelect("resetHighlights")}
                disabled={canvasOn}
              >
                Reset Highlights
              </MenuItem>
              <MenuItem
                onClick={() => handleToolSelect("resetCanvas")}
                disabled={!canvasOn}
              >
                Reset Canvas
              </MenuItem>
              {isDemoMode && (
                <MenuItem onClick={() => handleToolSelect("resetDemo")}>
                  Reset Demo
                </MenuItem>
              )}
            </Menu>
          </Box>
          <Box>
            <Tooltip title="Account">
              <Button
                onClick={handleProfileClick}
                sx={{ minWidth: 0, gap: 0, px: 1, color: "inherit" }}
              >
                <Avatar
                  src={userInfo?.avatar_url || undefined}
                  // No photo: render like the other toolbar icons —
                  // transparent fill, glyph/initials follow the theme text
                  // colour (black on light, white on dark).
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: "0.85rem",
                    bgcolor: "transparent",
                    color: "text.primary",
                  }}
                >
                  {accountInitials || (
                    // Match the other 24px toolbar icons.
                    <PersonOutlined sx={{ fontSize: 24 }} />
                  )}
                </Avatar>
                <ArrowDropDownOutlined sx={{ fontSize: "25px", ml: -0.75 }} />
              </Button>
            </Tooltip>
            <Menu
              anchorEl={profileAnchor}
              open={isProfileOpen}
              onClose={handleProfileClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              {userState.userId
                ? [
                    <Box key="account-header" sx={{ px: 2, py: 1, maxWidth: 260 }}>
                      <Typography variant="subtitle2" noWrap>
                        {accountName || "Account"}
                      </Typography>
                      {userInfo?.email && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: "block" }}
                        >
                          {userInfo.email}
                        </Typography>
                      )}
                    </Box>,
                    <Divider key="account-divider" />,
                    <MenuItem
                      key="workspaces"
                      onClick={() => {
                        navigate("/workspaces");
                        handleProfileClose();
                      }}
                    >
                      Workspaces
                    </MenuItem>,
                    <MenuItem
                      key="files"
                      onClick={() => {
                        navigate("/files");
                        handleProfileClose();
                      }}
                    >
                      Files
                    </MenuItem>,
                    <MenuItem
                      key="profile"
                      onClick={() => {
                        navigate("/profile");
                        handleProfileClose();
                      }}
                    >
                      Profile
                    </MenuItem>,
                    <MenuItem
                      key="learn"
                      onClick={() => {
                        navigate("/learn");
                        handleProfileClose();
                      }}
                    >
                      Learn
                    </MenuItem>,
                    <MenuItem
                      key="replay-tour"
                      onClick={() => {
                        dispatch(startTour());
                        handleProfileClose();
                      }}
                    >
                      Replay tour
                    </MenuItem>,
                    <Divider key="divider" />,
                    <MenuItem key="logout" onClick={handleLogout}>
                      Log Out
                    </MenuItem>,
                  ]
                : [
                    <MenuItem
                      key="learn"
                      onClick={() => {
                        navigate("/learn");
                        handleProfileClose();
                      }}
                    >
                      Learn
                    </MenuItem>,
                    <MenuItem
                      key="replay-tour"
                      onClick={() => {
                        dispatch(startTour());
                        handleProfileClose();
                      }}
                    >
                      Replay tour
                    </MenuItem>,
                    <Divider key="divider" />,
                    <MenuItem key="register" onClick={handleDemoQuit}>
                      Register
                    </MenuItem>,
                  ]}
            </Menu>
          </Box>
        </FlexBetween>
      </Toolbar>
      {modals(modalVisible, setModalVisible)}
    </AppBar>
  );
};

export default ArgNavbar;
